/**
 * POS Category Family Definitions
 *
 * Hierarchical navigation structure:
 * Family → Category → Animal Type → Products
 */

export interface CategoryFamily {
  color: string;
  hoverColor: string;
  categories: string[];
  icon: string;
}

export const FEED_FAMILIES: Record<string, CategoryFamily> = {
  "5 STAR": {
    color: "#B91C1C",
    hoverColor: "#991B1B",
    categories: ["5 STAR Bags", "5 STAR Ton", "5 STAR 1000", "BYOC 5 STAR"],
    icon: "⭐"
  },
  "PLUS": {
    color: "#1D4ED8",
    hoverColor: "#1E40AF",
    categories: ["PLUS Ton", "PLUS 1000", "PLUS bags"],
    icon: "➕"
  },
  "ESS": {
    color: "#15803D",
    hoverColor: "#166534",
    categories: ["ESS Bags", "ESS Ton", "ESS 1000"],
    icon: "🌿"
  },
  "Pet": {
    color: "#92400E",
    hoverColor: "#78350F",
    categories: ["DOG", "DOG TREATS", "Canned Dog Food", "CAT", "Canned Cat Food", "CAT LITTER", "WILD BIRD", "Bird Bars"],
    icon: "🐾"
  },
  "Specialty": {
    color: "#0D9488",
    hoverColor: "#0F766E",
    categories: ["Redmond", "Waterloo Ranch", "Red Bridge Laser Forge", "Blocks", "Steves Woodcrafts"],
    icon: "🏷️"
  },
  "Ingredients": {
    color: "#65A30D",
    hoverColor: "#4D7C0F",
    categories: ["Ingredient Bags", "Mill Ingredients", "Whole Grains"],
    icon: "🌾"
  },
  "Store": {
    color: "#FBBF24",
    hoverColor: "#F59E0B",
    categories: ["Store", "Retail Bags", "Shipment Packaging", "Wood", "DIRT"],
    icon: "🏪"
  }
};

// Families that have animal type sub-filters (feed categories)
export const FEED_CATEGORY_FAMILIES = ["5 STAR", "PLUS", "ESS"];

export const ANIMAL_TYPES = [
  "All",
  "All Flock",
  "Layer",
  "Grower",
  "Starter",
  "Turkey",
  "Gamebird",
  "Waterfowl",
  "Beef",
  "Dairy",
  "Swine",
  "Goat",
  "Sheep",
  "Rabbit",
  "Horse",
  "Other"
];

// Keywords to detect animal type from product name
export const ANIMAL_TYPE_KEYWORDS: Record<string, string[]> = {
  "All Flock": ["all flock", "flock", "all stock", "allstock", "poultry"],
  "Layer": ["layer", "laying"],
  "Grower": ["grower", "growing"],
  "Starter": ["starter", "start", "chick"],
  "Turkey": ["turkey"],
  "Gamebird": ["gamebird", "game bird", "pheasant", "quail"],
  "Waterfowl": ["waterfowl", "duck", "goose"],
  "Beef": ["beef", "cattle", "steer", "cow"],
  "Dairy": ["dairy", "milk"],
  "Swine": ["swine", "pig", "hog", "maintenance"],
  "Goat": ["goat"],
  "Sheep": ["sheep", "lamb"],
  "Rabbit": ["rabbit", "bunny"],
  "Horse": ["horse", "cavalry", "equine"],
};

/**
 * Detect animal type from product name
 */
export const detectAnimalType = (productName: string): string => {
  const name = productName.toLowerCase();

  for (const [animalType, keywords] of Object.entries(ANIMAL_TYPE_KEYWORDS)) {
    if (keywords.some(kw => name.includes(kw))) {
      return animalType;
    }
  }

  return "Other";
};

/**
 * Normalize a category name for comparison
 * Handles trailing/leading spaces and case differences
 */
export const normalizeCategory = (category: string | null | undefined): string => {
  if (!category) return '';
  return category.trim().toLowerCase();
};

/**
 * Check if a product's category matches a target category (normalized)
 */
export const categoryMatches = (
  productCategory: string | null | undefined,
  targetCategory: string
): boolean => {
  return normalizeCategory(productCategory) === normalizeCategory(targetCategory);
};

/**
 * Check if a product's category belongs to a family's categories (normalized)
 */
export const categoryInFamily = (
  productCategory: string | null | undefined,
  familyCategories: string[]
): boolean => {
  const normalized = normalizeCategory(productCategory);
  return familyCategories.some(cat => normalizeCategory(cat) === normalized);
};

/**
 * Get family name from category name (with normalized matching)
 */
export const getFamilyFromCategory = (categoryName: string): string | null => {
  const normalized = normalizeCategory(categoryName);
  for (const [family, data] of Object.entries(FEED_FAMILIES)) {
    if (data.categories.some(cat => normalizeCategory(cat) === normalized)) {
      return family;
    }
  }
  return null;
};

/**
 * Get available animal types from a list of products
 * Returns only types that have at least one matching product
 */
export const getAvailableAnimalTypes = (
  products: Array<{ name: string; category_name: string }>,
  selectedCategory?: string | null
): string[] => {
  // Filter to selected category if provided
  const categoryProducts = selectedCategory
    ? products.filter(p => categoryMatches(p.category_name, selectedCategory))
    : products;

  // Find which animal types exist in these products
  const typesFound = new Set<string>();
  categoryProducts.forEach(p => {
    const type = detectAnimalType(p.name);
    if (type && type !== 'Other') {
      typesFound.add(type);
    }
  });

  // Check if there are "Other" products (don't match any keyword)
  const hasOther = categoryProducts.some(p => detectAnimalType(p.name) === 'Other');

  // Build result: "All" first, then sorted types, then "Other" if exists
  const sortedTypes = Array.from(typesFound).sort();
  const result = ['All', ...sortedTypes];
  if (hasOther) {
    result.push('Other');
  }

  return result;
};
